import type { QuotaResponse } from '../../../lib/enrichment/api';

interface Props {
  quota: QuotaResponse | null;
}

export function EnrichmentQuotaTab({ quota }: Props) {
  if (!quota) {
    return <div className="text-[13px] text-[#787774] text-center py-8">Nessun dato quota disponibile.</div>;
  }

  const used = quota.quota.monthly_credits_used;
  const total = quota.quota.monthly_credits_quota;
  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
  const resetAt = quota.quota.monthly_reset_at ? new Date(quota.quota.monthly_reset_at) : null;

  // Storico per mese
  const byMonth = new Map<string, { credits: number; eur: number; runs: number }>();
  for (const r of quota.runs_history) {
    if (!r.completed_at) continue;
    const key = r.completed_at.slice(0, 7);
    const acc = byMonth.get(key) ?? { credits: 0, eur: 0, runs: 0 };
    acc.credits += Number(r.credits_consumed) || 0;
    acc.eur += Number(r.cost_eur_estimated) || 0;
    acc.runs += 1;
    byMonth.set(key, acc);
  }
  const history = Array.from(byMonth.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));

  // Per provider
  const byProvider = new Map<string, { credits: number; runs: number }>();
  for (const r of quota.runs_history) {
    const key = r.provider ?? 'unknown';
    const acc = byProvider.get(key) ?? { credits: 0, runs: 0 };
    acc.credits += Number(r.credits_consumed) || 0;
    acc.runs += 1;
    byProvider.set(key, acc);
  }

  const barColor = pct >= 90 ? 'bg-[#e03e3e]' : pct >= 70 ? 'bg-[#cb912f]' : 'bg-[#37352f]';

  return (
    <div className="space-y-6 text-[#37352f]">
      {/* Quota row */}
      <div className="border border-[rgba(55,53,47,0.09)] rounded-md p-4">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-[13px] font-medium">Quota mensile</span>
          {resetAt && (
            <span className="text-[11px] text-[#9b9a97]">
              reset il {resetAt.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-[20px] font-semibold tabular-nums">{used}</span>
          <span className="text-[13px] text-[#787774]">/ {total} crediti</span>
          <span className="text-[12px] text-[#9b9a97] ml-auto">{pct}%</span>
        </div>
        <div className="h-1.5 bg-[rgba(55,53,47,0.06)] rounded-full overflow-hidden">
          <div className={`h-full transition-all ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
        {pct >= 90 && (
          <div className="mt-3 text-[12px] text-[#e03e3e]">
            Quota quasi esaurita. Considera upgrade piano.
          </div>
        )}
      </div>

      {/* Storico mensile */}
      <div>
        <h3 className="text-[13px] font-medium mb-2">Consumo ultimi 6 mesi</h3>
        {history.length === 0 ? (
          <div className="text-[12px] text-[#787774] py-2">Nessun consumo registrato.</div>
        ) : (
          <div className="divide-y divide-[rgba(55,53,47,0.09)] border border-[rgba(55,53,47,0.09)] rounded-md">
            {history.map(([month, agg]) => (
              <div key={month} className="flex items-center justify-between px-3 py-2">
                <div>
                  <div className="text-[13px]">
                    {new Date(`${month}-01`).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                  </div>
                  <div className="text-[11px] text-[#9b9a97]">{agg.runs} arricchimenti</div>
                </div>
                <div className="text-right">
                  <div className="text-[13px] tabular-nums">{agg.credits} crediti</div>
                  <div className="text-[11px] text-[#9b9a97]">≈ €{agg.eur.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per provider */}
      <div>
        <h3 className="text-[13px] font-medium mb-2">Distribuzione per provider</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Array.from(byProvider.entries()).map(([provider, agg]) => (
            <div key={provider} className="border border-[rgba(55,53,47,0.09)] rounded-md px-3 py-2">
              <div className="text-[13px]">{providerLabel(provider)}</div>
              <div className="text-[11px] text-[#9b9a97] mt-0.5">
                {agg.runs} run · {agg.credits} crediti
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function providerLabel(provider: string): string {
  switch (provider) {
    case 'internal_parser':
      return 'Parser interno (gratuito)';
    case 'vies':
      return 'VIES (validazione P.IVA gratuita)';
    case 'openapi_it':
      return 'openapi.it (visura)';
    case 'dropcontact':
      return 'Dropcontact (persona B2B)';
    case 'fullenrich':
      return 'FullEnrich (telefono mobile)';
    default:
      return provider;
  }
}
